import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/constants';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  TextField,
  Grid,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Select,
  Box,
  Autocomplete,
  RadioGroup,
  Radio,
  FormLabel,
  FormHelperText,
  ListSubheader
} from '@mui/material';
import { format } from 'date-fns';
import theme from '../theme/theme';
import { ThemeProvider } from '@mui/material/styles';
import DraftSaveButton from '../components/DraftSaveButton';
import { DRAFT_KEYS, loadDraft, clearDraft, saveDraft, hasDraft } from '../utils/draftUtils';

import { useNotification } from '../contexts/NotificationContext';
import {
  surgeryData,
  surgeonList,
  assistantSurgeonList,
  anesthesiologistList,
  nursingStaffList,
  theatreOptions,
  allergiesOptions,
  insuranceOptions,
  roomNumbers
} from '../utils/dataConstants';

const formatDate = (date) => {
  return format(new Date(date), 'yyyy-MM-dd HH:mm:ss');
};

const OTBookingSlip = () => {
  const { showNotification } = useNotification();
  const [formData, setFormData] = useState({
    ip_no: "",
    uhid_no: "",
    patient_name: "",
    booking_datetime: "",
    allergies_medical_alerts: [],
    type_of_surgery: "",
    surgical_code: "",
    procedure_code: "",
    type_of_procedure: "",
    diagnosis_reason: "",
    expected_duration: "",
    surgery_datetime: "",
    preferred_operating_theatre: "",
    priority_status: "",
    primary_surgeon_name: "",
    primary_surgeon_id: "",
    assistant_surgeon_names: "",
    assistant_surgeon_ids: "",
    anesthesiologist_name: "",
    anesthesiologist_id: "",
    nursing_staff_names: "",
    nursing_staff_ids: "",
    preoperative_tests_required: false,
    special_equipment_needed: false,
    blood_products_required: false,
    fasting_instructions_given: false,
    preoperative_medications_provided: false,
    consent_forms_completed: false,
    is_major_ot: false,
    operation_status: "",
    room_no: "",
    insurance_details: "",
    payment_method: "",
    actual_start_time: "",
    online_payment_option: "",
    card_type: "",
    patient_id: "",
  });

  const [allergiesError, setAllergiesError] = useState(false);
  const [patients, setPatients] = useState([]);
  const [otBookings, setOtBookings] = useState([]);
  const [filteredPatients, setFilteredPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();
  const allergyInputRef = useRef(null);

  const hasFormData = () => {
    return Object.values(formData).some(value => {
      if (typeof value === 'string') return value.trim() !== '';
      if (typeof value === 'object' && value !== null) {
        if (Array.isArray(value)) return value.length > 0;
        return Object.values(value).some(subVal => typeof subVal === 'string' ? subVal.trim() !== '' : subVal);
      }
      return value === true;
    });
  };

  const saveFormData = (data) => {
    sessionStorage.setItem(`ot_booking_form_${data.uhid_no || 'default'}`, JSON.stringify(data));
  };

  const clearSavedData = () => {
    sessionStorage.removeItem(`ot_booking_form_${formData.uhid_no || 'default'}`);
    sessionStorage.removeItem(`formStatus_ot_booking_${formData.uhid_no || 'default'}`);
  };

  useEffect(() => {
    if (!dataLoaded) return;
    const timeoutId = setTimeout(() => {
      if (hasFormData() && formData.uhid_no) {
        saveFormData(formData);
      }
    }, 2000);
    return () => clearTimeout(timeoutId);
  }, [formData, dataLoaded]);

  useEffect(() => {
    const saved = sessionStorage.getItem(`ot_booking_form_${formData.uhid_no || 'default'}`);
    if (saved) {
      setFormData(JSON.parse(saved));
    } else {
      const now = new Date();
      setFormData(prev => ({ ...prev, booking_datetime: format(now, "yyyy-MM-dd'T'HH:mm") }));
    }
  }, []);

  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));

    const autoFillMap = {
      patient_name: { list: patients, matchField: "name", valueField: "uhid_no", idField: "uhid_no" },
      primary_surgeon_name: { list: surgeonList, matchField: "name", valueField: "id", idField: "primary_surgeon_id" },
      assistant_surgeon_names: { list: assistantSurgeonList, matchField: "name", valueField: "id", idField: "assistant_surgeon_ids" },
      anesthesiologist_name: { list: anesthesiologistList, matchField: "name", valueField: "id", idField: "anesthesiologist_id" },
      nursing_staff_names: { list: nursingStaffList, matchField: "name", valueField: "id", idField: "nursing_staff_ids" }
    };

    if (name === 'type_of_surgery') {
      const selected = surgeryData.find(s => s.diagnosisDescription === value);
      if (selected) {
        setFormData(prev => ({
          ...prev,
          type_of_surgery: value,
          surgical_code: selected.icd10Code,
          procedure_code: selected.procedureCode,
          type_of_procedure: selected.procedureDescription
        }));
      } else setFormData(prev => ({ ...prev, [name]: value }));
    } else if (autoFillMap[name]) {
      const cfg = autoFillMap[name];
      const selected = cfg.list.find(item => item[cfg.matchField] === value);
      setFormData(prev => ({ ...prev, [name]: value, [cfg.idField]: selected ? selected[cfg.valueField] : "" }));
    } else if (name === 'insurance_details') {
      setFormData(prev => ({ ...prev, insurance_details: value, payment_method: value ? 'Insurance' : prev.payment_method }));
    } else {
      setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const handleBlur = (event) => {
    const { name, value } = event.target;
    let error = "";
    const req = {
      ip_no: "IP No", uhid_no: "Patient Name", type_of_surgery: "Type of Surgery",
      diagnosis_reason: "Diagnosis Reason", expected_duration: "Expected Duration",
      preferred_operating_theatre: "Preferred Operating Theatre", priority_status: "Priority Status",
      primary_surgeon_name: "Primary Surgeon Name", anesthesiologist_name: "Anesthesiologist Name",
      nursing_staff_names: "Nursing Staff Name", payment_method: "Payment Method",
      room_no: "Room No", surgery_datetime: "Surgery Date & Time"
    };

    if (req[name] && !String(value).trim()) error = `${req[name]} is required`;
    if (name === "surgery_datetime" && value && new Date(value) < new Date()) error = "Surgery date cannot be in the past";
    if (name === "expected_duration" && value && Number(value) <= 0) error = "Duration must be greater than 0";

    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validate = () => {
    const temp = {};
    if (!formData.uhid_no) temp.uhid_no = 'Please select a patient';
    if (!String(formData.ip_no).trim()) temp.ip_no = 'IP Number is required';
    if (!formData.booking_datetime) temp.booking_datetime = 'Booking datetime required';
    if (!formData.surgery_datetime) temp.surgery_datetime = 'Surgery datetime required';
    else if (new Date(formData.surgery_datetime) < new Date()) temp.surgery_datetime = 'Surgery date cannot be in the past';

    if (!String(formData.type_of_surgery).trim()) temp.type_of_surgery = 'Surgery type required';
    if (!String(formData.diagnosis_reason).trim()) temp.diagnosis_reason = 'Diagnosis reason required';
    if (!formData.expected_duration || formData.expected_duration <= 0) temp.expected_duration = 'Valid duration required';
    if (!formData.preferred_operating_theatre) temp.preferred_operating_theatre = 'OT selection required';
    if (!formData.priority_status) temp.priority_status = 'Priority required';
    if (!formData.primary_surgeon_name) temp.primary_surgeon_name = 'Surgeon name required';
    if (!formData.anesthesiologist_name) temp.anesthesiologist_name = 'Anesthesiologist required';
    if (!formData.nursing_staff_names) temp.nursing_staff_names = 'Nursing staff required';
    if (!formData.payment_method) temp.payment_method = 'Payment method required';

    setErrors(temp);
    const hasError = Object.keys(temp).length > 0;
    if (hasError) {
      const firstError = Object.values(temp)[0];
      showNotification('error', `Required Field: ${firstError}`);
    }
    return !hasError;
  };

  const handleHomeNavigation = () => {
    if (hasFormData() && formData.uhid_no) {
      saveFormData(formData);
      sessionStorage.setItem(`formStatus_ot_booking_${formData.uhid_no}`, JSON.stringify({
        formName: 'OT BOOKING SLIP', uhid_no: formData.uhid_no, status: 'incomplete', lastSaved: new Date().toISOString(), continueUrl: `/ot-booking/${formData.uhid_no}`
      }));
    }
    navigate('/home', { state: { activeMenu: 'OT Booking' } });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;

    const typed = allergyInputRef.current?.value?.trim();
    let allergies = [...formData.allergies_medical_alerts];
    if (typed && !allergies.includes(typed)) allergies.push(typed);

    if (formData.payment_method === "Online" && !formData.online_payment_option) {
      showNotification('error', "Please select an Online Payment Option.");
      return;
    }
    if (formData.payment_method === "Online" && formData.online_payment_option === "card" && !formData.card_type) {
      showNotification('error', "Please select Card Type.");
      return;
    }

    const formatted = {
      ...formData,
      hospital_id: 1,
      allergies_medical_alerts: allergies.join(', '),
      surgery_duration: formData.expected_duration,
      surgery_datetime: formatDate(formData.surgery_datetime),
      assistant_surgeon_ids: formData.assistant_surgeon_ids ? parseInt(formData.assistant_surgeon_ids, 10) : 0,
      nursing_staff_ids: formData.nursing_staff_ids ? parseInt(formData.nursing_staff_ids) : 0,
      preoperative_tests_required: formData.preoperative_tests_required ? 1 : 0,
      special_equipment_needed: formData.special_equipment_needed ? 1 : 0,
      blood_products_required: formData.blood_products_required ? 1 : 0,
      fasting_instructions_given: formData.fasting_instructions_given ? 1 : 0,
      preoperative_medications_provided: formData.preoperative_medications_provided ? 1 : 0,
      consent_forms_completed: formData.consent_forms_completed ? 1 : 0,
      is_major_ot: formData.is_major_ot ? 1 : 0,
    };

    try {
      await axios.post(`${API_BASE_URL}/insert_ot_booking`, formatted, { withCredentials: true });
      showNotification('success', `OT Booking for ${formData.patient_name} submitted successfully!`);
      clearSavedData();
      setTimeout(() => navigate('/home', { state: { activeMenu: 'OT Booking' } }), 1000);
    } catch (error) {
      console.error('Submission error:', error);
      const msg = error.response?.data?.error || 'Error in submitting OT Booking!';
      showNotification('error', msg);
    }
  };

  // Fetch initial data
  useEffect(() => {
    const fetchOTBookings = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API_BASE_URL}/get_ot_bookings`, { withCredentials: true });
        const data = response.data?.ot_bookings;
        if (Array.isArray(data)) {
          setOtBookings(data);
        } else {
          showNotification('error', 'Invalid OT bookings data from server');
        }
      } catch (err) {
        console.error(err);
        showNotification('error', 'Failed to fetch OT bookings');
      } finally {
        setLoading(false);
        setDataLoaded(true);
      }
    };

    fetchOTBookings();
  }, [showNotification]);

  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_all_patients`, { withCredentials: true })
      .then(response => {
        setPatients(response.data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error fetching patients:', error);
        showNotification('error', 'Failed to fetch patient list');
        setLoading(false);
      });
  }, [showNotification]);

  useEffect(() => {
    if (patients.length > 0 && otBookings.length > 0) {
      const bookedUHIDs = new Set(otBookings.map(b => b.uhid_no));
      const availablePatients = patients.filter(p => !bookedUHIDs.has(p.uhid_no));
      setFilteredPatients(availablePatients);
    } else {
      setFilteredPatients(patients);
    }
  }, [patients, otBookings]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [ipRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/ot_booking_next_ip`, { withCredentials: true })
        ]);
        setFormData(prev => ({
          ...prev,
          ip_no: ipRes.data.next_ip_no
        }));
      } catch (error) {
        console.error('Failed to fetch initial data:', error);
      }
    };

    fetchInitialData();
  }, []);

  // Load draft if available
  useEffect(() => {
    if (formData.uhid_no && hasDraft(DRAFT_KEYS.ADD_OT_BOOKING, formData.uhid_no)) {
      const saved = loadDraft(DRAFT_KEYS.ADD_OT_BOOKING, formData.uhid_no);
      if (saved && saved.formData) {
        setFormData(prev => ({ ...prev, ...saved.formData }));
      }
    }
  }, [formData.uhid_no]);



  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 3 }}>
          <button
            onClick={() => navigate('/home', { state: { activeMenu: 'OT Booking' } })}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              color: '#008080',
              fontSize: '16px',
              fontWeight: 500,
              cursor: 'pointer',
              marginRight: '16px',
            }}
          >
            ← Back
          </button>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: 2 }}>
            <Typography
              variant="h4"
              sx={{
                color: '#008080',
                display: 'flex',
                alignItems: 'center',
                fontWeight: 600,
                letterSpacing: 0.5,
              }}
            >
              OT Booking Slip
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <img src="/logo.png" alt="Logo" className="w-8 h-8" />
              <Typography
                variant="h6"
                sx={{
                  color: '#008080',
                  marginLeft: 1,
                  fontWeight: 500,
                  letterSpacing: 0.3,
                }}
              >
                OTMS
              </Typography>
            </Box>
          </Box>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Patient Details */}
              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  IP No <span style={{ color: 'red' }}>*</span>
                </Typography>
                <TextField
                  name="ip_no"
                  type="number"
                  value={formData.ip_no}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.ip_no}
                  helperText={errors.ip_no}
                  onBlur={handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Patient Name <span style={{ color: 'red' }}>*</span>
                </Typography>
                <Autocomplete
                  options={filteredPatients}
                  getOptionLabel={(option) => option.name || ""}
                  renderOption={(props, option) => (
                    <li {...props}>
                      {option.name} – UHID: {option.uhid_no}
                    </li>
                  )}
                  value={selectedPatient}
                  onChange={(_, newValue) => {
                    setSelectedPatient(newValue);
                    setFormData(prev => ({
                      ...prev,
                      patient_name: newValue?.name || "",
                      uhid_no: newValue?.uhid_no || "",
                      patient_id: newValue?.id || ""
                    }));
                    if (newValue) setErrors(prev => ({ ...prev, uhid_no: "" }));
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      fullWidth
                      error={!!errors.uhid_no}
                      helperText={errors.uhid_no}
                      onBlur={() => {
                        if (!formData.uhid_no) setErrors(prev => ({ ...prev, uhid_no: "Patient is required" }));
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Date & Time of Booking (Optional)"
                  name="booking_datetime"
                  type="datetime-local"
                  value={formData.booking_datetime}
                  InputLabelProps={{ shrink: true }}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.booking_datetime}
                  helperText={errors.booking_datetime}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>UHID No <span style={{ color: 'red' }}>*</span></span>}
                  name="uhid_no"
                  value={formData.uhid_no}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                >
                  {patients.map((patient) => (
                    <MenuItem key={patient.uhid_no} value={patient.uhid_no}>
                      {patient.uhid_no}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  freeSolo
                  options={allergiesOptions}
                  value={formData.allergies_medical_alerts || []}
                  onChange={(event, newValue) => {
                    handleChange({ target: { name: 'allergies_medical_alerts', value: newValue } });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Any Allergies or Medical Alerts"
                      placeholder="Select or type allergies"
                      fullWidth
                      inputRef={allergyInputRef}
                    />
                  )}
                />
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth error={!!errors.type_of_surgery}>
                  <InputLabel id="type-of-surgery-label">
                    <span>Type of Surgery/Procedure <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    labelId="type-of-surgery-label"
                    id="type-of-surgery"
                    value={formData.type_of_surgery || ''}
                    label={<span>Type of Surgery/Procedure <span style={{ color: 'red' }}>*</span></span>}
                    onChange={(event) =>
                      handleChange({
                        target: { name: 'type_of_surgery', value: event.target.value },
                      })
                    }
                    onBlur={handleBlur}
                  >
                    {surgeryData.map((surgery, index) => (
                      <MenuItem key={index} value={surgery.diagnosisDescription}>
                        {surgery.diagnosisDescription}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText error>{errors.type_of_surgery || 'Select surgery to auto-fill codes'}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Surgical Code (ICD-10) <span style={{ color: 'red' }}>*</span></span>}
                  name="surgical_code"
                  value={formData.surgical_code}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Procedure Code (CPT) <span style={{ color: 'red' }}>*</span></span>}
                  name="procedure_code"
                  value={formData.procedure_code}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label={<span>Procedure Description <span style={{ color: 'red' }}>*</span></span>}
                  name="type_of_procedure"
                  value={formData.type_of_procedure}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  helperText="Auto-filled when surgery is selected"
                />
              </Grid>
              <Grid item xs={12} >
                <TextField
                  label={<span>Date & Time of Surgery <span style={{ color: 'red' }}>*</span></span>}
                  name="surgery_datetime"
                  InputLabelProps={{ shrink: true }}
                  type="datetime-local" inputProps={{
                    min: new Date().toISOString().slice(0, 16), // Sets the minimum date and time to the current date and time
                  }}
                  value={formData.surgery_datetime}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.surgery_datetime}
                  helperText={errors.surgery_datetime}
                  onBlur={handleBlur}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={<span>Diagnosis/Reason for Surgery <span style={{ color: 'red' }}>*</span></span>}
                  name="diagnosis_reason"
                  value={formData.diagnosis_reason}
                  onChange={handleChange}
                  fullWidth
                  error={!!errors.diagnosis_reason}
                  helperText={errors.diagnosis_reason}
                  onBlur={handleBlur}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label="Expected Duration (hours)"
                  name="expected_duration"
                  type="number"
                  inputProps={{ min: 0.5, step: 0.5 }}
                  value={formData.expected_duration}
                  onChange={handleChange}
                  required
                  fullWidth
                  error={!!errors.expected_duration}
                  helperText={errors.expected_duration}
                  onBlur={handleBlur}
                />
              </Grid>

              {/* Preferred Theatre & Priority */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>Preferred Operating Theatre <span style={{ color: 'red' }}>*</span></span>}
                  name="preferred_operating_theatre"
                  value={formData.preferred_operating_theatre || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  error={!!errors.preferred_operating_theatre}
                  helperText={errors.preferred_operating_theatre}
                  onBlur={handleBlur}
                >
                  {theatreOptions.map((option, index) => (
                    <MenuItem key={index} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth error={!!errors.priority_status}>
                  <InputLabel>
                    <span>Priority Status <span style={{ color: 'red' }}>*</span></span>
                  </InputLabel>
                  <Select
                    label={<span>Priority Status <span style={{ color: 'red' }}>*</span></span>}
                    name="priority_status"
                    value={formData.priority_status}
                    onChange={handleChange}
                    onBlur={handleBlur}
                  >
                    <MenuItem value="Emergency">Emergency</MenuItem>
                    <MenuItem value="Urgent">Urgent</MenuItem>
                    <MenuItem value="Elective">Elective</MenuItem>
                  </Select>
                  <FormHelperText error>{errors.priority_status || ' '}</FormHelperText>
                </FormControl>
              </Grid>

              {/* Surgeon & Staff Details */}
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>Primary Surgeon Name <span style={{ color: 'red' }}>*</span></span>}
                  name="primary_surgeon_name"
                  value={formData.primary_surgeon_name}
                  onChange={handleChange}
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.primary_surgeon_name}
                  helperText={errors.primary_surgeon_name}
                >
                  {surgeonList.map((s) => (
                    <MenuItem key={s.id} value={s.name}>{s.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Primary Surgeon ID <span style={{ color: 'red' }}>*</span></span>}
                  name="primary_surgeon_id"
                  type="number"
                  value={formData.primary_surgeon_id}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>Assistant Surgeon Name(s) <span style={{ color: 'red' }}>*</span></span>}
                  name="assistant_surgeon_names"
                  value={formData.assistant_surgeon_names}
                  onChange={handleChange}
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.assistant_surgeon_names}
                  helperText={errors.assistant_surgeon_names}
                >
                  {assistantSurgeonList.map((a) => (
                    <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Assistant Surgeon ID(s) <span style={{ color: 'red' }}>*</span></span>}
                  name="assistant_surgeon_ids"
                  type="number"
                  value={formData.assistant_surgeon_ids}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>Anesthesiologist Name <span style={{ color: 'red' }}>*</span></span>}
                  name="anesthesiologist_name"
                  value={formData.anesthesiologist_name}
                  onChange={handleChange}
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.anesthesiologist_name}
                  helperText={errors.anesthesiologist_name}
                >
                  {anesthesiologistList.map((a) => (
                    <MenuItem key={a.id} value={a.name}>{a.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Anesthesiologist ID <span style={{ color: 'red' }}>*</span></span>}
                  name="anesthesiologist_id"
                  type="number"
                  value={formData.anesthesiologist_id}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  label={<span>Nursing Staff Name <span style={{ color: 'red' }}>*</span></span>}
                  name="nursing_staff_names"
                  value={formData.nursing_staff_names}
                  onChange={handleChange}
                  fullWidth
                  onBlur={handleBlur}
                  error={!!errors.nursing_staff_names}
                  helperText={errors.nursing_staff_names}
                >
                  {nursingStaffList.map((n) => (
                    <MenuItem key={n.id} value={n.name}>{n.name}</MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Nursing Staff ID(s) <span style={{ color: 'red' }}>*</span></span>}
                  name="nursing_staff_ids"
                  type="number"
                  value={formData.nursing_staff_ids}
                  onChange={handleChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
              </Grid>

              {/* Preoperative and Consent Information */}
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.preoperative_tests_required} onChange={handleChange} name="preoperative_tests_required" />}
                  label="Preoperative Tests/Reports Required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.special_equipment_needed} onChange={handleChange} name="special_equipment_needed" />}
                  label="Special Equipment Needed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.blood_products_required} onChange={handleChange} name="blood_products_required" />}
                  label="Blood Products Required"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.fasting_instructions_given} onChange={handleChange} name="fasting_instructions_given" />}
                  label="Fasting Instructions Given"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.preoperative_medications_provided} onChange={handleChange} name="preoperative_medications_provided" />}
                  label="Preoperative Medications Provided"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.consent_forms_completed} onChange={handleChange} name="consent_forms_completed" />}
                  label="Consent Forms Completed"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={<Checkbox checked={formData.is_major_ot} onChange={handleChange} name="is_major_ot" />}
                  label="Major OT / Minor OT"
                />
              </Grid>

              {/* Insurance and Payment */}
              <Grid item xs={12}>
                <Autocomplete
                  options={insuranceOptions}
                  value={formData.insurance_details || ''}
                  onChange={(event, newValue) => {
                    // When user selects an option from dropdown or clears it
                    handleChange({ target: { name: 'insurance_details', value: newValue || '' } });
                  }}
                  onInputChange={(event, newInputValue) => {
                    // When user types or edits input directly
                    handleChange({ target: { name: 'insurance_details', value: newInputValue || '' } });
                  }}
                  freeSolo // allow custom input, not limited to options
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Insurance Details"
                      variant="outlined"
                      fullWidth
                    />
                  )}
                />

              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth required>
                  <InputLabel>Payment Method</InputLabel>
                  <Select
                    label="Payment Method"
                    name="payment_method"
                    value={formData.payment_method}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={!!errors.payment_method}
                  >
                    <MenuItem value="Cash" disabled={!!formData.insurance_details}>Cash</MenuItem>
                    <MenuItem value="Insurance">Insurance</MenuItem>
                    <MenuItem value="Online" disabled={!!formData.insurance_details}>Online</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {formData.payment_method === "Online" && (
                <Box display="flex" flexDirection="column" sx={{ mt: '14px', ml: 2 }}>
                  <FormLabel sx={{ fontSize: '0.75rem', color: 'rgba(0, 0, 0, 0.6)', mb: 0 }}>
                    Select Online Payment Method
                  </FormLabel>
                  <RadioGroup
                    row
                    value={formData.online_payment_option}
                    onChange={(e) =>
                      setFormData({ ...formData, online_payment_option: e.target.value, card_type: "" })
                    }
                  >
                    <FormControlLabel value="upi" control={<Radio />} label="UPI" />
                    <FormControlLabel value="card" control={<Radio />} label="Card" />
                    <FormControlLabel value="netbanking" control={<Radio />} label="Net Banking" />
                  </RadioGroup>

                  {/* Show Card Type only if Card is selected */}
                  {formData.online_payment_option === "card" && (
                    <FormControl sx={{ mt: 2 }}>
                      <FormLabel>{<span>Card Type <span style={{ color: 'red' }}>*</span></span>}</FormLabel>
                      <RadioGroup
                        row
                        value={formData.card_type}
                        onChange={(e) => setFormData({ ...formData, card_type: e.target.value })}
                      >
                        <FormControlLabel value="Debit" control={<Radio />} label="Debit Card" />
                        <FormControlLabel value="Credit" control={<Radio />} label="Credit Card" />
                      </RadioGroup>
                    </FormControl>
                  )}
                </Box>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={<span>Patient Room No / Ward <span style={{ color: 'red' }}>*</span></span>}
                  select

                  name="room_no"
                  value={formData.room_no || ''}
                  onChange={handleChange}
                  variant="outlined"
                  fullWidth
                  required
                  error={!!errors.room_no}
                  helperText={errors.room_no}
                  onBlur={handleBlur}
                >
                  {roomNumbers.map((room) => (
                    <MenuItem key={room} value={room}>
                      {room}
                    </MenuItem>
                  ))}
                </TextField>

              </Grid>

              <Grid item xs={12}>
                {/* Save Draft Button */}
                <DraftSaveButton
                  formKey={DRAFT_KEYS.ADD_OT_BOOKING}
                  uhidNo={formData.uhid_no}
                  formData={formData}
                  onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                />
                <Button type="submit" variant="contained" color="primary" sx={{ marginTop: 3 }} fullWidth>
                  Submit OT Booking
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default OTBookingSlip;